import {
  NutrientStatus,
  PreviewWarning,
} from '../../../services/feedAnalysisService';

const MONTHS = [
  'jan',
  'fev',
  'mar',
  'abr',
  'mai',
  'jun',
  'jul',
  'ago',
  'set',
  'out',
  'nov',
  'dez',
];

const MONTHS_LONG = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];

export function formatNumber(
  value: number | null | undefined,
  digits = 2,
): string {
  if (value === null || value === undefined) return '';
  return value.toLocaleString('pt-BR', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

// diferença com sinal, como na planilha (+1,10 / -2,83)
export function formatDifference(value: number | null | undefined): string {
  if (value === null || value === undefined) return '';
  return `${value > 0 ? '+' : ''}${formatNumber(value)}`;
}

export function formatPercent(
  value: number | null | undefined,
  digits = 1,
): string {
  if (value === null || value === undefined) return '—';
  return `${formatNumber(value * 100, digits)}%`;
}

// "-10% / +10%"
export function formatTolerance(below: number, above: number): string {
  return `-${formatNumber(below * 100, 0)}% / +${formatNumber(
    above * 100,
    0,
  )}%`;
}

export function formatDate(value: string | null | undefined): string {
  if (!value) return '';
  const [year, month, day] = value.slice(0, 10).split('-');
  return `${day}/${month}/${year}`;
}

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return '';
  return `${formatDate(value)} ${value.slice(11, 16)}`.trim();
}

// "2026-07-01" | "2026-07" -> "jul/26"
export function monthShort(value: string): string {
  const [year, month] = value.split('-');
  return `${MONTHS[Number(month) - 1]}/${year.slice(2)}`;
}

// "2026-07-01" -> "Julho 2026"
export function monthLong(value: string): string {
  const [year, month] = value.split('-');
  return `${MONTHS_LONG[Number(month) - 1]} ${year}`;
}

// "2026-07-01" -> "2026-07" (valor do <input type="month">)
export const monthInput = (value: string): string => value.slice(0, 7);

export function currentMonth(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

export function shiftMonth(month: string, amount: number): string {
  const [year, number] = month.split('-').map(Number);
  const index = year * 12 + number - 1 + amount;
  return `${Math.floor(index / 12)}-${String((index % 12) + 1).padStart(
    2,
    '0',
  )}`;
}

// mesmas cores e símbolos da legenda da planilha
export const STATUS_STYLE: Record<
  NutrientStatus,
  { label: string; symbol: string; bg: string; color: string; scheme: string }
> = {
  ideal: {
    label: 'Ideal',
    symbol: '✔',
    bg: 'green.500',
    color: 'white',
    scheme: 'green',
  },
  below: {
    label: 'Abaixo',
    symbol: '▼',
    bg: 'yellow.400',
    color: 'gray.900',
    scheme: 'yellow',
  },
  above: {
    label: 'Acima',
    symbol: '▲',
    bg: 'red.500',
    color: 'white',
    scheme: 'red',
  },
  no_reference: {
    label: 'Sem ref.',
    symbol: '–',
    bg: 'gray.600',
    color: 'gray.100',
    scheme: 'gray',
  },
};

// aviso da importação: rótulo e se impede gravar a linha
export const WARNING_INFO: Record<
  PreviewWarning,
  { label: string; blocking: boolean }
> = {
  missing_sample_code: { label: 'Sem código da amostra', blocking: true },
  duplicate: { label: 'Amostra já importada', blocking: true },
  feed_not_found: { label: 'Ração não cadastrada', blocking: true },
  no_formula: { label: 'Ração sem fórmula', blocking: false },
  no_reference: { label: 'Sem referência no mês', blocking: false },
  no_month: { label: 'Sem mês da análise', blocking: true },
  no_results: { label: 'Nenhum resultado lido', blocking: true },
};
